import { STAGE_LABEL } from './vocabulary.ts'

/**
 * Finding the requisição somebody is holding, from inside a conferência (#501).
 *
 * The bench is driven by paper: the next thing to confer is the label in the
 * conferente's hand, not the next row of the queue. This asks the queue's own
 * search for it, so the barcode is decoded where it already is (the server,
 * since #491) and the answer is scoped to what this person may see.
 */
export interface RequisitionMatch {
  readonly branchCode: number
  readonly branchName?: string | undefined
  readonly quotationNumber: number
  readonly patientName: string
  /**
   * False for a filial the ERP has the number at and the queue does not:
   * choosing it fetches the requisição before opening it.
   */
  readonly inQueue: boolean
  /** Known once the ERP fetch created it; opens it where no filial was named. */
  readonly verificationId?: string | undefined
}

export const GoToStatus = {
  Idle: 'idle',
  Searching: 'searching',
  /** Not in the queue: the ERP is being asked for it. */
  Fetching: 'fetching',
  /** Not a number and not a scan: there is nothing to look up. */
  Invalid: 'invalid',
  NotFound: 'not-found',
  /** The search itself did not answer. */
  Failed: 'failed',
  /** The ERP fetch was refused, and the server said why. */
  Refused: 'refused',
  /** The same number in more than one filial: the person says which. */
  Choosing: 'choosing',
} as const
export type GoToStatus = (typeof GoToStatus)[keyof typeof GoToStatus]

export type GoToState =
  | {
      readonly status: Exclude<GoToStatus, typeof GoToStatus.Choosing | typeof GoToStatus.Refused>
    }
  | { readonly status: typeof GoToStatus.Refused; readonly message: string }
  | {
      readonly status: typeof GoToStatus.Choosing
      readonly matches: readonly RequisitionMatch[]
      readonly active: number
    }

export const GO_TO_MESSAGE: Readonly<Partial<Record<GoToStatus, string>>> = {
  [GoToStatus.Invalid]: 'Digite o número ou leia o código de barras',
  [GoToStatus.NotFound]: 'Requisição não encontrada',
  [GoToStatus.Failed]: 'Não foi possível buscar. Tente de novo.',
}

/** The slice of the queue's search this needs; `api.queue` fits it. */
export type SearchRequisitions = (params: {
  q: string
  stage: string
  sort: string
  dir: string
  limit: number
}) => Promise<{
  readonly verifications: ReadonlyArray<{
    readonly branch_code: number
    readonly branch_name?: string
    readonly quotation_number: number
    readonly patient_name: string
  }>
  readonly scanned?: { readonly branchCode: number; readonly quotationNumber: number } | null
}>

/** What was asked for, as the ERP fetch needs it: a scan names its filial. */
export interface WantedRequisition {
  readonly quotationNumber: number
  readonly branchCode?: number | undefined
}

export type Lookup =
  | { readonly status: typeof GoToStatus.Invalid }
  | { readonly status: typeof GoToStatus.NotFound; readonly wanted: WantedRequisition }
  | { readonly status: typeof GoToStatus.Choosing; readonly matches: readonly RequisitionMatch[] }

/*
 * Every stage: the paper in hand is wherever it is on the bench, and the
 * queue's default leaves produção out.
 */
const EVERY_STAGE = Object.keys(STAGE_LABEL).join(',')

/**
 * Smallest numbers first, so a page is enough.
 *
 * The queue matches a typed number as a substring -- "8174" also finds 817467
 * -- and only the exact one is wanted here. Every number that contains the
 * digits typed is at least as large as the number typed, so in ascending order
 * the exact matches lead and cannot be pushed off the first page.
 */
const PAGE = { sort: 'quotation_number', dir: 'asc', limit: 50 } as const

export async function findRequisitions(raw: string, search: SearchRequisitions): Promise<Lookup> {
  const typed = raw.trim()
  if (!/^\d+$/.test(typed)) return { status: GoToStatus.Invalid }

  const page = await search({ q: typed, stage: EVERY_STAGE, ...PAGE })
  // A scan is matched exactly by the server; a typed number is narrowed here.
  const wanted = page.scanned?.quotationNumber ?? Number(typed)

  const matches = new Map<string, RequisitionMatch>()
  for (const row of page.verifications) {
    if (row.quotation_number !== wanted) continue
    matches.set(`${row.branch_code}/${row.quotation_number}`, {
      branchCode: row.branch_code,
      branchName: row.branch_name,
      quotationNumber: row.quotation_number,
      patientName: row.patient_name,
      inQueue: true,
    })
  }
  if (matches.size === 0) {
    return {
      status: GoToStatus.NotFound,
      wanted: { quotationNumber: wanted, branchCode: page.scanned?.branchCode },
    }
  }
  return { status: GoToStatus.Choosing, matches: [...matches.values()] }
}

/** Which option a key lands on in the list of filiais; it wraps at both ends. */
export function movedActive(active: number, count: number, key: string): number {
  if (count === 0) return 0
  if (key === 'ArrowDown') return (active + 1) % count
  if (key === 'ArrowUp') return (active - 1 + count) % count
  return active
}
